import { FC } from "react";
import { Page } from "../../templates/Page";
import { P1 } from "../../atoms/Paragraph";
import { H1 } from "../../atoms/Heading";
import { StandardSection } from "../../templates/StandardSection";
import { TrustpilotMini } from "components/atoms/TrustpilotMini";
import { CustomBullet } from "../../atoms/CustomBulletImage";
import { GetAQuoteForm } from "./GetAQuoteForm";
import { Grid, Li, LogoFlex } from "./styles";
import { mobile, MobileOnly, NotMobile } from "components/templates/Breakpoints";
import styled from "styled-components";
import { useTranslations } from "next-intl";
import { useLocale } from "hooks";
import data from "./translations";
import { useLocaleData } from "hooks/useLocaleData";
import { LatestCaseStudy } from "utils/server";
import Image from "next/image";
import { PurpleVideoSection } from "components/molecules/PurpleVideoSection";

interface GetAQuoteProps {
  latestCaseStudy: LatestCaseStudy;
}

const GetAQuote: FC<GetAQuoteProps> = ({ latestCaseStudy }) => {
  const t = useTranslations("get-a-quote");
  const { locale } = useLocale();
  const { bulletFontColour, logos } = useLocaleData(data);
  return (
    <>
      <Page
        navColor="transparent"
        hero={null}
        fullScreenHero
        latestCaseStudy={latestCaseStudy}
        showAnnouncementBar
      >
        <StandardSection>
          <Grid>
            <div>
              <H1>{t<string>("title")}</H1>
              <P1 pt="1.5rem" pb="0.5rem">
                {t<string>("subtitle")}
              </P1>
              <ul>
                <Li color={bulletFontColour}>
                  <CustomBullet
                    src={t<string>("bullet-points.first.bullet-image")}
                    alt={t<string>("bullet-points.first.alt")}
                  />
                  {t<string>("bullet-points.first.copy")}
                </Li>
                <Li color={bulletFontColour}>
                  <CustomBullet
                    src={t<string>("bullet-points.second.bullet-image")}
                    alt={t<string>("bullet-points.second.alt")}
                  />
                  {t<string>("bullet-points.second.copy")}
                </Li>
                <Li color={bulletFontColour}>
                  <CustomBullet
                    src={t<string>("bullet-points.third.bullet-image")}
                    alt={t<string>("bullet-points.third.alt")}
                  />
                  {t<string>("bullet-points.third.copy")}
                </Li>
              </ul>

              <NotMobile>
                {locale !== "za" ? (
                  <>
                    <StyledP1 pt="2.5rem">
                      <strong>Trusted by:</strong>
                    </StyledP1>
                    <LogoFlex>
                      {logos.map((logo) => {
                        const { width, src, alt } = logo;
                        return <Image width={width} height={50} src={src} alt={alt} key={alt} />;
                      })}
                    </LogoFlex>{" "}
                  </>
                ) : null}

                {locale === "us" ? (
                  <TrustpilotMini score={5} copy="Employees say" />
                ) : (
                  <TrustpilotMini score={5} />
                )}
              </NotMobile>
            </div>
            <GetAQuoteForm />
            <MobileOnly>
              {locale !== "za" ? (
                <>
                  <StyledP1 pt="2.5rem">
                    <strong>Trusted by:</strong>
                  </StyledP1>
                  <LogoFlex>
                    {logos.map((logo) => {
                      const { width, src, alt } = logo;
                      return <Image width={width} height={50} src={src} alt={alt} key={alt} />;
                    })}
                  </LogoFlex>
                </>
              ) : null}
              {locale === "us" ? (
                <TrustpilotMini score={5} copy="Employees say" />
              ) : (
                <TrustpilotMini score={5} />
              )}
            </MobileOnly>
          </Grid>
        </StandardSection>
        <PurpleVideoSection
          heading={t<string>("video-section.title")}
          body={[t("video-section.body1"), t("video-section.body2")]}
          imageSrc={t<string>("video-section.imageSrc")}
          vimeoId={t<string>("video-section.vimeoId")}
          buttonText="Get a quote"
        />
      </Page>
    </>
  );
};

export default GetAQuote;

const StyledP1 = styled(P1)`
  ${mobile} {
    padding-top: 0rem;
  }
`;
